import { Button } from "@/components/ui/button";
import { TooltipProvider, Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { File, Download, X } from 'lucide-react';
import { useAttachments } from "./AttachmentContext";

const AttachmentDeleteButton = ({ attachment, className = "" }) => {
    const { setAttachmentToDelete, setDeleteModalOpen } = useAttachments();

    return (
        <Button
            type="button"
            size="icon"
            variant="ghost"
            className={className}
            onClick={(e) => {
                e.stopPropagation();
                setAttachmentToDelete(attachment);
                setDeleteModalOpen(true);
            }}
            tabIndex={-1}
        >
            <X className="h-3 w-3" />
        </Button>
    );
};

const ImageAttachment = ({ attachment }) => {
    const { handleOpenAttachment, getAttachmentUrl } = useAttachments();
    const imageUrl = getAttachmentUrl(attachment);

    return (
        <div 
            className="relative w-32 h-28 rounded-lg overflow-hidden border bg-neutral-100 dark:bg-neutral-900 group cursor-pointer"
            onClick={() => handleOpenAttachment(imageUrl)}
        >
            <img
                src={imageUrl}
                alt={attachment.filename || attachment.name}
                className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-black/10" />
            
            <div className="absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/50 to-transparent">
                <div className="text-xs text-white truncate">
                    {attachment.filename || attachment.name}
                </div>
            </div>
            <div className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <AttachmentDeleteButton 
                    attachment={attachment} 
                    className="h-6 w-6 bg-black/50 hover:bg-black/70 text-white border-none"
                />
            </div>
        </div>
    );
};

const FileAttachment = ({ attachment }) => {
    const { handleOpenAttachment } = useAttachments();

    return (
        <div 
            className="relative w-32 h-28 rounded-lg border bg-neutral-100 dark:bg-neutral-900 flex flex-col items-center justify-center p-3 gap-2 group cursor-pointer hover:bg-neutral-200 dark:hover:bg-neutral-800 transition-all duration-200 hover:shadow-md"
            onClick={() => handleOpenAttachment(attachment.url)}
        >
            <div className="flex flex-col items-center gap-1 transform group-hover:scale-105 transition-transform duration-200">
                <File className="h-8 w-8 text-neutral-400 group-hover:text-neutral-600 dark:group-hover:text-neutral-300" />
                <div className="text-xs text-center text-neutral-600 dark:text-neutral-400 line-clamp-2 w-full max-w-[90%]">
                    {attachment.filename || attachment.name}
                </div>
                <Download className="h-3.5 w-3.5 text-neutral-400 group-hover:text-neutral-600 dark:group-hover:text-neutral-300 mt-1" />
            </div>
            <div className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <AttachmentDeleteButton 
                    attachment={attachment} 
                    className="h-6 w-6"
                />
            </div>
        </div>
    );
};

const MinimalAttachmentItem = ({ attachment, index }) => {
    const { getAttachmentUrl, setAttachmentToDelete, setDeleteModalOpen } = useAttachments();
    const imageUrl = getAttachmentUrl(attachment);

    return (
        <li
            key={attachment.key || attachment.id || index}
            className="flex items-center gap-2 py-1 min-w-0"
        >
            {/* Thumbnail or icon */}
            {attachment.contentType && attachment.contentType.startsWith('image/') && imageUrl ? (
                <img
                    src={imageUrl}
                    alt={attachment.name || attachment.filename}
                    className="w-4 h-4 object-cover rounded border flex-shrink-0"
                />
            ) : (
                <File className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
            )}
            {/* File name */}
            <div className="flex-1 min-w-0">
                <TooltipProvider delayDuration={0}>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="truncate text-xs text-muted-foreground block">
                                {attachment.name || attachment.filename}
                            </span>
                        </TooltipTrigger>
                        <TooltipContent>{attachment.name || attachment.filename}</TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </div>
            {/* Remove button */}
            <Button
                type="button"
                size="icon"
                variant="ghost"
                className="h-4 w-4 text-muted-foreground hover:text-foreground"
                onClick={() => {
                    setAttachmentToDelete(attachment);
                    setDeleteModalOpen(true);
                }}
                tabIndex={-1}
            >
                ×
            </Button>
        </li>
    );
};

export default function AttachmentList({ style = "grid" }) {
    const { task } = useAttachments();
    
    if (!task?.attachments?.length) {
        return null;
    }

    if (style === "minimal") {
        return (
            <div className="space-y-1">
                {task.attachments.map((attachment, index) => (
                    <MinimalAttachmentItem 
                        key={attachment.key || attachment.id || index}
                        attachment={attachment} 
                        index={index} 
                    />
                ))}
            </div>
        );
    }

    // Grid style for existing tasks
    return (
        <div className="relative">
            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-neutral-200 dark:scrollbar-thumb-neutral-800">
                {task.attachments.map((attachment, index) => (
                    <div key={index} className="flex-shrink-0 group relative">
                        {attachment.contentType?.startsWith('image/') ? (
                            <ImageAttachment attachment={attachment} />
                        ) : (
                            <FileAttachment attachment={attachment} />
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
} 